/* Owns the side panel. 560 px on the right by default, a full-screen bottom
   sheet with a drag handle below md. A drawer opened as a route must pop the
   route rather than only hide itself, so the back button and the close button
   agree — that is the caller's `onOpenChange`. */

import { X } from 'lucide-react'
import { useCallback, useId, type ReactNode } from 'react'

import { cn } from './cn.js'
import { useFocusTrap } from './useFocusTrap.js'

export interface DrawerProps {
  open: boolean
  onOpenChange: (o: boolean) => void
  side?: 'right' | 'bottom'
  width?: number
  title: string
  description?: string
  footer?: ReactNode
  children: ReactNode
  className?: string
  'data-testid'?: string
}

export function Drawer({
  open,
  onOpenChange,
  side = 'right',
  width = 560,
  title,
  description,
  footer,
  children,
  className,
  'data-testid': testId,
}: DrawerProps): ReactNode {
  const titleId = useId()
  const descriptionId = useId()
  const close = useCallback(() => onOpenChange(false), [onOpenChange])
  const ref = useFocusTrap<HTMLDivElement>({ open, onClose: close })

  if (!open) return null

  const bottom = side === 'bottom'

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={close} aria-hidden />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        data-testid={testId}
        tabIndex={-1}
        style={bottom ? undefined : { width: `min(${width}px, 100vw)` }}
        className={cn(
          'absolute flex flex-col border-border-strong bg-elevated shadow-pop',
          // Below md every side drawer becomes a bottom sheet.
          bottom
            ? 'inset-x-0 bottom-0 max-h-[85vh] rounded-t-xl border-t'
            : 'inset-x-0 bottom-0 max-h-[85vh] rounded-t-xl border-t md:inset-y-0 md:right-0 md:left-auto md:max-h-none md:rounded-none md:border-l md:border-t-0',
          className,
        )}
      >
        <div aria-hidden className="mx-auto mt-2 h-1 w-10 rounded-full bg-border-strong md:hidden" />
        <header className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="text-lg font-medium text-text">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="text-sm text-text-muted">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="close"
            className="rounded-md p-1 text-text-faint hover:bg-panel hover:text-text"
          >
            <X aria-hidden className="size-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer ? <footer className="flex justify-end gap-2 border-t border-border px-4 py-3">{footer}</footer> : null}
      </div>
    </div>
  )
}
